import { h, render, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import clsx from 'clsx';
import moment from 'moment';
import CompletedIcon from '../utils/icons/completed.svg';
import './lifesharp-service-status.css';

export default (element, data) => {
  const codes = [
    'INITIATED',
    'LEG1_IN_TRANSIT',
    'LEG2_IN_TRANSIT',
    'SERVICING_IN_PROGRESS',
    'SERVICING_COMPLETE',
    'LEG3_IN_TRANSIT',
    'LEG3_DELIVERED',
  ];
  const steps = data.statuses;
  const codeDelayed = 'SERVICING_DELAY_PARTS';

  const LoadingSnippet = () => {
    return (
      <div
        role="status"
        className="w-full py-4 flex items-center justify-center"
      >
        <svg
          aria-hidden="true"
          class="w-8 h-8 text-grey-400 animate-spin dark:text-gray-600 fill-grey-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    );
  };

  const OrderTimeline = ({ data, delay }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [statuses, setStatuses] = useState([]);

    useEffect(() => {
      // Simulate a 0.5s loading time
      const timer = setTimeout(() => setIsLoading(false), parseInt(delay, 10));
      return () => clearTimeout(timer);
    }, [delay]);

    useEffect(() => {
      setStatuses(
        codes.map((status) => {
          return {
            ...data.find((item) => item.code === status),
            ...steps.find((item) => item.code === status),
            title: steps.find((step) => step.code === status)?.title,
          };
        }),
      );
    }, [data]);

    const getFormattedTime = (createdAt) => {
      const now = moment(createdAt);
      return now.format('hh:mm A - MMMM D YYYY');
    };

    if (isLoading) {
      return <LoadingSnippet />;
    }

    return (
      <div className="flex flex-col laptop:flex-row items-start laptop:items-start justify-between gap-y-[20px] relative pt-5 px-5 laptop:px-0">
        {statuses.map((item, index) => {
          const imageData = (steps || []).find((icon) =>
            item?.delay
              ? icon?.code === codeDelayed
              : icon?.code === item?.code,
          )?.image;

          const isDone = item?.created_at;

          const ImageComponent = () => {
            if (!imageData) return null;
            const isSVG = imageData.src
              .split('?')[0]
              .toLowerCase()
              .endsWith('.svg');

            return isSVG ? (
              <div
                className="w-6 h-6"
                style={{
                  WebkitMaskImage: `url(${imageData.src})`,
                  maskImage: `url(${imageData.src})`,
                  backgroundColor: item?.delay ? '#873100' : '#12D301',
                }}
              />
            ) : (
              <img
                className="w-6 h-6"
                src={imageData.src}
                alt={imageData.alt || item?.title}
                width={24}
                height={24}
              />
            );
          };

          return (
            <div key={index} className="flex-1 relative h-full">
              <div className="flex flex-row laptop:flex-col items-center gap-x-2.5">
                <div
                  className={clsx(
                    'w-[40px] h-[40px] rounded-full flex items-center justify-center text-black font-bold relative bg-[#F5F5F5] z-2',
                    {
                      'border border-[#12D301] animate-slide-in':
                        isDone && !item?.delay,
                      'border border-[#873100] animate-pulse': item?.delay,
                      'animate-slide-in': !isDone,
                    },
                  )}
                  style={{
                    animationDelay: `${index * 200}ms`,
                    opacity: 0,
                  }}
                >
                  {isDone && ImageComponent()}
                </div>
                <div
                  className="mt-2 text-sm laptop:text-base animate-slide-in text-left laptop:text-center"
                  style={{ animationDelay: `${index * 200}ms`, opacity: 0 }}
                >
                  <span className="block text-xs pb-[2px]">{item.title}</span>
                  {item?.created_at && (
                    <span className="block text-[.625rem] text-secondary-1">
                      {getFormattedTime(item.created_at)}
                    </span>
                  )}
                  {item?.delay && item?.note && (
                    <span className="block text-[.625rem] text-[#873100]">
                      {item.note}
                    </span>
                  )}
                </div>

                {index < statuses.length - 1 && (
                  <div
                    className="block laptop:hidden w-[1px] h-[1000px] bg-[#12D301] absolute top-1/2 left-[20px] origin-top animate-grow-line-vertical"
                    style={{ animationDelay: `${index * 200}ms`, height: 0 }}
                  />
                )}

                {index < statuses.length - 1 && (
                  <div
                    className={clsx(
                      'hidden laptop:block h-[1px]  w-[1000px] absolute top-[20px] left-1/2 z-1 origin-left animate-grow-line-horizontal',
                      {
                        'bg-[#12D301]': isDone,
                        'bg-[#F5F5F5]': !isDone,
                      },
                    )}
                    style={{ animationDelay: `${index * 200}ms`, width: 0 }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const AccordionItem = ({ data, key, completed }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [orderStatus, setOrderStatus] = useState('');

    const toggleAccordion = () => {
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      const lastStatus = (data?.items[0]?.statuses || [])
        .filter((status) => codes.includes(status.code))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setOrderStatus(
        steps.find((step) => step.code === lastStatus[0]?.code)?.title,
      );
    }, [data]);

    return (
      <div className="w-full" key={key}>
        <>
          <button
            className="px-5 py-5 laptop:px-[30px] accordion-trigger flex items-center justify-between bg-gray-100 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus:ring-gray-500 border-b-[1px] border-grey-400"
            aria-expanded={isOpen}
            aria-controls="section-{index}-content"
            onClick={toggleAccordion}
          >
            {!completed && (
              <span>
                <span className="text-sm laptop:text-base uppercase font-med font-[450] tracking-[-.2px] pr-9 laptop:pr-5">
                  Order #: {data.name}
                </span>
                <span className="text-[.625rem] laptop:text-sm">
                  {orderStatus}
                </span>
              </span>
            )}

            {completed && (
              <>
                <span className="text-sm laptop:text-base uppercase font-med font-[450] tracking-[-.2px] pr-9 laptop:pr-5">
                  Order #: {data.name}
                </span>
                <span className="text-[.625rem] laptop:text-sm flex items-center justify-center gap-x-2">
                  <span>Completed</span>
                  <span className="text-[#12D301]">
                    <CompletedIcon />
                  </span>
                </span>
              </>
            )}

            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-300 transform ${
                isOpen ? 'rotate-180' : ''
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <div
            id="section-{index}-content"
            className={`accordion-content overflow-hidden transition-[max-height] duration-500 border-b-[1px] border-grey-400 ${
              isOpen ? 'max-h-max' : 'max-h-0'
            }`}
            aria-labelledby="section-{index}-header"
            hidden={!isOpen}
          >
            {(data.items || []).map((item, index) => (
              <div
                className="py-5 laptop:py-[30px] border-b-[1px] border-grey-400"
                key={index}
              >
                <div className="px-5 laptop:px-[30px] text-left flex flex-col gap-y-[10px] laptop:gap-y-[5px]">
                  <div className="text-base laptop:text-[1.375rem] uppercase font-med font-[450] tracking-[-.2px]">
                    Knife #{index + 1}
                  </div>
                  <div className="text-xs laptop:text-sm">Model {item.sku}</div>
                </div>

                {isOpen && (
                  <OrderTimeline
                    data={item.statuses}
                    delay={500 * (index + 1)}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      </div>
    );
  };

  const LifeSharpServiceStatus = () => {
    const [ordersData, setOrdersData] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUrl = async (url) => {
      const response = await fetch(url, {
        method: 'GET',
        'Content-Type': 'application/json',
        headers: {
          Authorization: `Bearer Zy(k}]Ja{sQ}"FsfBj)gUq:[T(Ud3{E)`,
        },
      });
      if (!response.ok) {
        return null;
      }
      return response.text();
    };

    useEffect(() => {
      const getOrderData = async (order) => {
        const { orderNumber } = order;
        const url = `${data.apiBaseUrl}/api/proxies/service-hub/${orderNumber}/statuses`;
        const response = await fetchUrl(url);
        return JSON.parse(response);
      };

      async function getOrdersData() {
        const ordersInProgress = [];
        const ordersInCompleted = [];

        await data?.customerOrders?.reduce(async (promise, order) => {
          await promise;
          const response = await getOrderData(order);
          if (response) {
            const { order } = response;
            if (order?.items.length > 0) {
              let deliveredCount = 0;
              order?.items?.forEach((item) => {
                const deliveredStatus = item?.statuses.find(
                  (status) => status.code === 'LEG3_DELIVERED',
                );
                if (deliveredStatus) deliveredCount += 1;
              });
              if (deliveredCount === order?.items?.length)
                ordersInCompleted.push(order);
              else ordersInProgress.push(order);
            }
          }
        }, Promise.resolve());

        setIsLoading(false);
        setOrdersData(ordersInProgress);
        setHistoryData(ordersInCompleted);

        return false;
      }
      getOrdersData();
    }, []);

    if (isLoading) {
      return <LoadingSnippet />;
    }

    return (
      <>
        {ordersData.length > 0 && (
          <div className="rounded-[10px] bg-white overflow-hidden shadow-custom-light mb-5">
            <div className="flex flex-col laptop:flex-row items-start laptop:items-center justify-start gap-x-[10px] gap-y-[4px] px-5 py-5 laptop:px-[30px] border-b-[1px] border-grey-400">
              <h2 className="text-base laptop:text-[1.375rem] uppercase font-med font-[450] tracking-[-.2px]">
                Service Status
              </h2>
              <span className="text-base">Hi, {data.firstName}!</span>
            </div>

            {ordersData.map((order, index) => (
              <AccordionItem data={order} key={index} completed={false} />
            ))}
          </div>
        )}

        {historyData.length > 0 && (
          <div className="rounded-[10px] bg-white overflow-hidden shadow-custom-light">
            <div className="flex flex-col laptop:flex-row items-start laptop:items-center justify-start gap-x-[10px] gap-y-[4px] px-5 py-5 laptop:px-[30px] border-b-[1px] border-grey-400">
              <h2 className="text-base laptop:text-[1.375rem] uppercase font-med font-[450] tracking-[-.2px]">
                Service History
              </h2>
            </div>

            {historyData.map((order, index) => (
              <AccordionItem data={order} key={index} completed={true} />
            ))}
          </div>
        )}
      </>
    );
  };

  render(<LifeSharpServiceStatus />, element);
};
